import { useEffect, useState, useMemo, MouseEvent } from "react";
import {
  SKIP_VALUE,
  Statement,
  Stored,
  StudentOpinion,
  PopulatedVotingSession,
} from "../../types";
import ExplainedStatement from "../explained_statement";
import { translate } from "../../translations";
import APIclient from "../../api-client";

export default function StatementComponent({
  statement,
  session,
  onStatementAnswered,
}: {
  statement: Stored<Statement>;
  session: PopulatedVotingSession;
  onStatementAnswered: (answer: Stored<StudentOpinion>) => void;
}) {
  const [opinion, setOpinion] = useState<string | null>(null);
  const [comment, setComment] = useState<string>("");
  const showArgument = useMemo(
    () =>
      session.argumentsEnabled && opinion !== null && opinion !== SKIP_VALUE,
    [opinion, session.argumentsEnabled],
  );

  useEffect(() => {
    if (!showArgument && opinion !== null) submitOpinion();
  }, [opinion, showArgument]);

  async function submitOpinion(event?: MouseEvent) {
    event?.preventDefault();
    if (opinion === null) return;

    const answer = await APIclient.opinions.create({
      sessionId: session._id,
      statementId: statement._id,
      opinion,
      comment,
    });
    onStatementAnswered(answer);
  }

  return (
    <form className="voting-session__statement">
      <ExplainedStatement statement={statement.description} />

      {opinion === null && (
        <fieldset
          className="opinion-question"
          aria-labelledby="explainedStatement"
        >
          {statement.opinionOptions
            .sort((a, b) => a.order - b.order)
            .map((option) => (
              <button
                className="opinion-question__button button"
                key={option.opinion}
                name="statement[opinion]"
                value={option.opinion}
                onClick={() => setOpinion(option.opinion)}
                style={{ "--opinion-color": option.color }}
              >
                {option.opinion}
              </button>
            ))}
          {session.skipQuestions && (
            <button
              className="opinion-question__skip"
              name="opinion"
              value={SKIP_VALUE}
              onClick={() => setOpinion(SKIP_VALUE)}
            >
              {translate("student.session.skipQuestion")}
            </button>
          )}
        </fieldset>
      )}
      {showArgument && (
        <div className="comment-question">
          <label htmlFor="statementComment" className="comment-question__label">
            {translate("student.session.comment.label")}{" "}
            <span className="">{opinion!.toLowerCase()}</span>?
          </label>
          <textarea
            name="statement[comment]"
            id="statementComment"
            value={comment}
            className="comment-question__textarea"
            onChange={(e) => setComment(e.target.value)}
          ></textarea>
          <span className="comment-question__character-counter">
            {translate("student.session.comment.characterCounter", {
              used: comment.length,
              total: 300,
            })}
          </span>

          <button
            type="submit"
            className="button button--full-width comment-question__submit"
            onClick={submitOpinion}
          >
            {translate("student.session.comment.submit")}
          </button>
        </div>
      )}
    </form>
  );
}
